import React, { useEffect, useRef } from 'react';
import WEBAPI from '@/services/WEBAPI';

// Renders an iframe and posts the public payment form into it.
// Avoids state updates inside effects; uses refs to guard duplicate submits/callbacks.
const PublicPaymentWebView = ({ authority, paymentId, onError, onComplete }) => {
  const formRef = useRef(null);
  const iframeNameRef = useRef(
    `public-payment-frame-${paymentId || 'unknown'}`
  );
  const submittedRef = useRef(false);
  const completedRef = useRef(false);
  const errorReportedRef = useRef(false);

  // Submit the hidden form once when authority/paymentId are available.
  useEffect(() => {
    if (!authority || !paymentId) {
      if (!errorReportedRef.current) {
        errorReportedRef.current = true;
        console.log('[PublicPaymentWebView] Missing authority or paymentId', {
          authority,
          paymentId,
        });
        onError?.('Missing authority or payment id');
      }
      return;
    }

    errorReportedRef.current = false;

    if (submittedRef.current) return;
    const formEl = formRef.current;
    if (!formEl) return;
    submittedRef.current = true;
    formEl.submit();
  }, [authority, paymentId, onError]);

  // Listen for postMessage events from the payment gateway.
  useEffect(() => {
    if (!authority || !paymentId) return;
    if (typeof window === 'undefined') return;

    const messageQueue = [];

    const handleMessage = (event) => {
      if (completedRef.current) return;
      try {
        const raw = event?.data;
        console.log('[PublicPaymentWebView] message event', {
          origin: event?.origin,
          data: raw,
        });
        const payload =
          typeof raw === 'string'
            ? JSON.parse(raw)
            : typeof raw === 'object'
            ? raw
            : null;

        if (!payload) return;

        const { error: responseError, result, code, status } = payload;

        if (responseError) {
          console.log('[PublicPaymentWebView] payment error', payload);
          completedRef.current = true;
          onError?.(result || responseError);
          return;
        }

        const isSuccessful =
          status === 'success' ||
          code === 'success' ||
          typeof result !== 'undefined';

        if (!isSuccessful) return;

        console.log('[PublicPaymentWebView] payment success', payload);
        completedRef.current = true;
        onComplete?.();
      } catch (err) {
        // ignore malformed messages
        console.log('[PublicPaymentWebView] message parse error', err);
      }
    };

    const handleEarlyMessage = (event) => {
      messageQueue.push(event);
    };

    window.addEventListener('message', handleEarlyMessage);

    const timer = setTimeout(() => {
      window.removeEventListener('message', handleEarlyMessage);
      window.addEventListener('message', handleMessage);
      messageQueue.forEach((event) => handleMessage(event));
    }, 500);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleEarlyMessage);
    };
  }, [authority, paymentId, onComplete, onError]);

  if (!authority || !paymentId) return null;

  const { url } = WEBAPI.createBaseUrl(authority);
  const actionUrl = `${url}payments/public/pay`;

  return (
    <div className="flex flex-1 flex-col relative">
      <div className="flex flex-col sm:m-10 m-0 sm:max-h-[calc(100vh-10rem)] sm:py-8 py-2 sm:px-8 px-4 flex-1 h-fit bg-white rounded-sm">
        <iframe
          name={iframeNameRef.current}
          title="Public Payment"
          className="w-full h-full rounded-sm"
          sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
          allow="payment *; fullscreen *; clipboard-write *"
        />
        <form
          ref={formRef}
          method="POST"
          action={actionUrl}
          target={iframeNameRef.current}
          style={{ display: 'none' }}
        >
          <input type="hidden" name="payment_id" value={paymentId} />
        </form>
      </div>
    </div>
  );
};

export default PublicPaymentWebView;
